import { useEffect, useState } from "react";

/**
 * Whether a Staffing view is offered in the chosen department, as a THIRD state rather than a boolean.
 * `"loading"` is a real answer: everything the per-view predicates derive from (the chosen department,
 * its jobs, its type) reads off sentinel defaults until the department resolves, so a boolean would say
 * "unavailable" for a department that in fact offers the view — which redirected the user out of the
 * view mid-hydration and built the redirect target from an empty `department_id`.
 *
 * Every Staffing view-availability hook returns this, and its route guard waits on `"loading"` instead
 * of deciding, so the selector's offer and the route's guard can never disagree.
 */
export type StaffingViewAvailability = "loading" | "available" | "unavailable";

/**
 * Turns a view's availability into "should the view-type dropdown offer this option", latching the
 * answer for as long as the same department stays chosen.
 *
 * An option that appears, vanishes and reappears mid-interaction is worse than one that arrives late,
 * and availability dips back to `"loading"` whenever the department re-resolves. So a resolved
 * `"available"` latches, and only a resolved `"unavailable"` (or a different department) clears it.
 * Because the matching route guard renders a loading state rather than redirecting while `"loading"`,
 * a latched offer can never become an offer-then-redirect bounce: the two read the one predicate.
 */
export const useLatchedViewOffer = (availability: StaffingViewAvailability, departmentId: string): boolean => {
	const [offeredForDepartmentId, setOfferedForDepartmentId] = useState<string | null>(null);

	useEffect(() => {
		if (availability === "available") setOfferedForDepartmentId(departmentId);
		else if (availability === "unavailable") setOfferedForDepartmentId(null);
	}, [availability, departmentId]);

	// `"unavailable"` is answered from the argument, not from the latch, because the effect that clears
	// the latch runs AFTER this render: reading the latch alone kept offering the option for one render
	// past the view becoming unreachable, which is long enough to click. The route guard consults the
	// same predicate and would already be redirecting — exactly the offer-then-redirect bounce this hook
	// exists to prevent.
	if (availability === "unavailable") return false;
	return availability === "available" || offeredForDepartmentId === departmentId;
};
